import React from "react";

/**
 * Vector PPF coverage diagram — blueprint side + top views of a sedan with
 * panel-accurate seam lines. Every coverage zone is individually addressable
 * and receives a translucent blue overlay with a visible boundary (never a
 * body recolour). Zone ids are shared with the backend package definitions
 * (pricing.py → PPF_PACKAGES[].zones): one source of truth for the diagram,
 * package cards, pricing, booking snapshot and CRM.
 *
 * Glass (windshield, side windows, rear window) and unpainted trim are drawn
 * in a distinct dark tone and are NEVER highlighted.
 */

const COVER_FILL = "rgba(31, 94, 255, 0.42)";
const COVER_STROKE = "#7db1ff";
const BODY_FILL = "#191d26";
const BODY_STROKE = "#4a5162";
const SEAM = "#3a4150";
const GLASS_FILL = "#0a0f1a";
const GLASS_STROKE = "#263145";
const WHEEL = "#0b0d12";

// ---------------------------------------------------------------------------
// SIDE VIEW (800x280, nose left). Greenhouse glass sits under the zones.
// ---------------------------------------------------------------------------
const SIDE_BODY =
  "M52,196 C52,182 62,172 78,168 L120,150 C190,140 268,134 330,132 L352,130 " +
  "L424,96 L560,92 L640,140 L740,150 C754,156 762,172 762,196 L760,214 " +
  "C762,228 754,244 736,244 L660,244 A46,46 0 0 0 568,244 L232,244 " +
  "A46,46 0 0 0 140,244 L72,244 C58,244 50,232 52,214 Z";

// one glasshouse polygon: windshield + side windows + rear window
const SIDE_GLASS = [
  "M352,130 L424,96 L560,92 L632,136 L640,144 L352,144 Z",
];

const SIDE_ZONES = {
  "front-bumper": "M52,196 C52,182 62,172 78,168 L108,160 L108,244 L72,244 C58,244 50,232 52,214 Z",
  "headlight-l": "M74,168 L108,160 L112,174 L78,180 Z",
  "hood-full": "M120,150 C190,140 268,134 330,132 L344,131 L344,148 L118,166 Z",
  "hood-24": "M120,150 C152,144 190,141 222,138 L222,156 L118,166 Z",
  // fender band above the front wheel arch, up to the door seam
  "fender-full-l": "M118,166 L344,148 L344,196 Q290,164 214,166 Q146,170 118,200 Z",
  "fender-front-l": "M118,166 L222,158 L222,186 Q180,170 140,180 Q122,186 118,200 Z",
  "mirror-l": "M338,116 L362,110 L366,124 L344,130 Z",
  "a-pillar-l": "M352,130 L424,96 L438,95 L366,131 Z",
  "b-pillar-l": "M466,94 L478,94 L478,146 L466,146 Z",
  "c-pillar-l": "M556,92 L572,92 L632,136 L612,138 Z",
  "roof-leading-edge": "M424,96 L560,92 L560,102 L430,106 Z",
  "roof-main": "M430,106 L560,102 L560,111 L436,115 Z",
  "door-front-l": "M344,146 L466,146 L466,224 L340,224 Z",
  "door-rear-l": "M478,146 L612,146 L636,224 L478,224 Z",
  "door-cups": "M420,170 L452,170 L452,184 L420,184 Z M492,170 L524,170 L524,184 L492,184 Z",
  "quarter-l": "M636,146 L740,150 C754,156 762,172 762,196 L700,200 Q680,178 640,178 Q606,180 598,200 L636,224 L644,190 Z",
  "trunk-lid": "M640,140 L740,150 C754,156 761,166 762,182 L700,170 Z",
  "rear-bumper": "M700,200 L762,196 L760,214 C762,228 754,244 736,244 L660,244 L668,224 L750,224 Z",
  "rockers": "M232,232 L568,232 L568,244 L232,244 Z",
};

// ---------------------------------------------------------------------------
// TOP VIEW (800x340, nose left)
// ---------------------------------------------------------------------------
const TOP_BODY =
  "M64,170 C64,124 96,106 150,100 L340,84 C420,78 540,80 610,90 L724,112 " +
  "C756,122 772,142 772,170 C772,198 756,218 724,228 L610,250 C540,260 420,262 340,256 " +
  "L150,240 C96,234 64,216 64,170 Z";

// windshield + rear window (side glass is out of the top outline)
const TOP_GLASS = [
  "M356,104 L400,100 L400,240 L356,236 Z",   // windshield
  "M566,110 L612,118 L612,222 L566,230 Z",   // rear window
];

const TOP_ZONES = {
  "front-bumper": "M64,170 C64,124 96,106 150,100 L150,240 C96,234 64,216 64,170 Z",
  "headlight-l": "M96,120 L150,108 L154,126 L102,136 Z",
  "headlight-r": "M96,220 L150,232 L154,214 L102,204 Z",
  "hood-full": "M150,116 L356,104 L356,236 L150,224 Z",
  "hood-24": "M150,116 L228,110 L228,230 L150,224 Z",
  "fender-full-l": "M150,100 L340,84 L356,86 L356,104 L150,116 Z",
  "fender-full-r": "M150,224 L356,236 L356,254 L340,256 L150,240 Z",
  "fender-front-l": "M150,100 L228,93 L228,110 L150,116 Z",
  "fender-front-r": "M150,224 L228,230 L228,247 L150,240 Z",
  "mirror-l": "M356,72 L388,78 L384,92 L354,88 Z",
  "mirror-r": "M356,268 L388,262 L384,248 L354,252 Z",
  "a-pillar-l": "M356,86 L400,84 L400,100 L356,104 Z",
  "a-pillar-r": "M356,236 L400,240 L400,256 L356,254 Z",
  "roof-leading-edge": "M400,100 L424,102 L424,238 L400,240 Z",
  "roof-main": "M424,102 L566,110 L566,230 L424,238 Z",
  "b-pillar-l": "M474,92 L490,93 L490,108 L474,107 Z",
  "b-pillar-r": "M474,233 L490,232 L490,247 L474,248 Z",
  "c-pillar-l": "M566,110 L612,118 L606,132 L566,126 Z",
  "c-pillar-r": "M566,230 L612,222 L606,208 L566,214 Z",
  "door-front-l": "M400,84 L474,92 L474,107 L400,100 Z",
  "door-front-r": "M400,240 L474,233 L474,248 L400,256 Z",
  "door-rear-l": "M490,93 L566,102 L566,118 L490,108 Z",
  "door-rear-r": "M490,232 L566,222 L566,238 L490,247 Z",
  "quarter-l": "M566,102 L640,112 L700,124 L640,124 L566,118 Z",
  "quarter-r": "M566,238 L640,228 L700,216 L640,216 L566,222 Z",
  "trunk-lid": "M612,118 L700,124 L730,130 L730,210 L700,216 L612,222 Z",
  "rear-bumper": "M730,130 C756,138 772,150 772,170 C772,190 756,202 730,210 Z",
  "rockers": "M360,82 L566,92 L566,96 L360,86 Z M360,254 L566,244 L566,248 L360,258 Z",
  "door-cups": "M448,89 L470,90 L470,96 L448,95 Z M448,244 L470,245 L470,251 L448,250 Z",
};

const ZONE_LABELS = {
  "front-bumper": "Front bumper",
  "hood-24": 'Hood — first 24"',
  "hood-full": "Full hood",
  "fender-front-l": "Front fender — partial (driver)",
  "fender-front-r": "Front fender — partial (passenger)",
  "fender-full-l": "Front fender — full (driver)",
  "fender-full-r": "Front fender — full (passenger)",
  "headlight-l": "Headlight (driver)",
  "headlight-r": "Headlight (passenger)",
  "mirror-l": "Mirror (driver)",
  "mirror-r": "Mirror (passenger)",
  "a-pillar-l": "A-pillar (driver)",
  "a-pillar-r": "A-pillar (passenger)",
  "b-pillar-l": "B-pillar (driver)",
  "b-pillar-r": "B-pillar (passenger)",
  "c-pillar-l": "C-pillar (driver)",
  "c-pillar-r": "C-pillar (passenger)",
  "roof-leading-edge": "Roof leading edge",
  "roof-main": "Roof",
  "door-front-l": "Front door (driver)",
  "door-front-r": "Front door (passenger)",
  "door-rear-l": "Rear door (driver)",
  "door-rear-r": "Rear door (passenger)",
  "door-cups": "Door handle cups",
  "quarter-l": "Rear quarter (driver)",
  "quarter-r": "Rear quarter (passenger)",
  "rear-bumper": "Rear bumper",
  "trunk-lid": "Trunk lid",
  "rockers": "Rocker panels",
};

function ZoneLayer({ zones, active }) {
  return Object.entries(zones).map(([id, d]) => (
    <path key={id} d={d} data-zone={id}
      fill={active.includes(id) ? COVER_FILL : "rgba(255,255,255,0.035)"}
      stroke={active.includes(id) ? COVER_STROKE : SEAM}
      strokeWidth={active.includes(id) ? 2 : 1}
      strokeLinejoin="round" />
  ));
}

export default function CoverageDiagram({ zones = [], testid = "coverage" }) {
  const active = Array.isArray(zones) ? zones : [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid={`${testid}-diagram`}>
      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="font-mono-alt text-[9px] uppercase tracking-widest text-white/30 mb-1">Side view</div>
        <svg viewBox="0 0 800 280" className="w-full h-auto" data-testid={`${testid}-side`} role="img" aria-label="PPF coverage — side view">
          <path d={SIDE_BODY} fill={BODY_FILL} stroke={BODY_STROKE} strokeWidth="2" />
          {SIDE_GLASS.map((d, i) => (
            <path key={i} d={d} fill={GLASS_FILL} stroke={GLASS_STROKE} strokeWidth="1" />
          ))}
          <ZoneLayer zones={SIDE_ZONES} active={active} />
          {/* wheels above the panel layer */}
          <circle cx="186" cy="222" r="42" fill={WHEEL} stroke="#333947" strokeWidth="3" />
          <circle cx="614" cy="222" r="42" fill={WHEEL} stroke="#333947" strokeWidth="3" />
          <circle cx="186" cy="222" r="18" fill="#181c24" stroke="#3a4150" strokeWidth="2" />
          <circle cx="614" cy="222" r="18" fill="#181c24" stroke="#3a4150" strokeWidth="2" />
        </svg>
      </div>
      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        <div className="font-mono-alt text-[9px] uppercase tracking-widest text-white/30 mb-1">Top view</div>
        <svg viewBox="0 0 800 340" className="w-full h-auto" data-testid={`${testid}-top`} role="img" aria-label="PPF coverage — top view">
          <path d={TOP_BODY} fill={BODY_FILL} stroke={BODY_STROKE} strokeWidth="2" />
          {TOP_GLASS.map((d, i) => (
            <path key={i} d={d} fill={GLASS_FILL} stroke={GLASS_STROKE} strokeWidth="1" />
          ))}
          <ZoneLayer zones={TOP_ZONES} active={active} />
        </svg>
      </div>
    </div>
  );
}

export { ZONE_LABELS };
